import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Edit3 } from "lucide-react";
import { api, apiErrToStr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { HtmlEditor } from "./HtmlEditor";
import { ImageUploader } from "./ImageUploader";
import { F } from "./F";

const EMPTY = { dham: "", state: "", title: "", title_hi: "", image: "", summary: "", summary_hi: "", best_time: "", best_time_hi: "", route_tips: "", route_tips_hi: "", content: "", content_hi: "", faqs: [], seo_title: "", seo_desc: "", published: true };

export function GuidesTab() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/admin/guides").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => {
    if (!window.confirm("Delete this guide?")) return;
    await api.delete(`/admin/guides/${id}`);
    toast.success("Deleted");
    load();
  };
  return (
    <div className="mt-4">
      <div className="flex justify-end mb-3">
        <Button data-testid="admin-new-guide" onClick={() => setEditing({ ...EMPTY })} className="bg-gold text-indigo-deep hover:bg-gold-dark hover:text-white"><Plus className="w-4 h-4 mr-1" />New guide</Button>
      </div>
      <div className="bg-white rounded-xl border border-gold/15 divide-y">
        {rows.map((g) => (
          <div key={g.id} data-testid={`admin-guide-${g.slug}`} className="p-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="font-semibold text-indigo-deep truncate">{g.title}</div>
              <div className="text-xs text-indigo-deep/60">{g.dham} · /guides/{g.slug} · {g.faqs?.length || 0} FAQs {g.published ? "" : "· draft"}</div>
            </div>
            <div className="flex gap-1 shrink-0">
              <Button size="sm" variant="ghost" data-testid={`edit-guide-${g.slug}`} onClick={() => setEditing(g)}><Edit3 className="w-4 h-4" /></Button>
              <Button size="sm" variant="ghost" data-testid={`delete-guide-${g.slug}`} onClick={() => del(g.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="p-8 text-center text-indigo-deep/60">No guides</div>}
      </div>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        {editing && <GuideForm guide={editing} onClose={() => { setEditing(null); load(); }} />}
      </Dialog>
    </div>
  );
}

function GuideForm({ guide, onClose }) {
  const [f, setF] = useState(guide);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const inp = (k) => (e) => set(k)(e.target.value);
  const setFaq = (i, k, v) => setF((p) => ({ ...p, faqs: p.faqs.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));

  const save = async () => {
    if (!f.title || !f.dham) return toast.error("Title and dham are required");
    const payload = { ...f, seo_title: f.seo_title || null, seo_desc: f.seo_desc || null };
    try {
      if (f.id) await api.put(`/admin/guides/${f.id}`, payload);
      else await api.post("/admin/guides", payload);
      toast.success("Guide saved");
      onClose();
    } catch (err) { toast.error(apiErrToStr(err)); }
  };

  return (
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-offwhite">
      <DialogHeader><DialogTitle className="font-display text-xl text-indigo-deep">{f.id ? "Edit" : "New"} travel guide</DialogTitle></DialogHeader>
      <Tabs defaultValue="en">
        <TabsList><TabsTrigger value="en" data-testid="gtab-en">English</TabsTrigger><TabsTrigger value="hi" data-testid="gtab-hi">हिंदी</TabsTrigger><TabsTrigger value="faq" data-testid="gtab-faq">FAQs</TabsTrigger><TabsTrigger value="seo" data-testid="gtab-seo">SEO</TabsTrigger></TabsList>
        <TabsContent value="en" className="space-y-3 mt-3">
          <div className="grid grid-cols-2 gap-3">
            <F label="Dham / destination"><Input data-testid="guide-dham" value={f.dham} onChange={inp("dham")} /></F>
            <F label="State"><Input value={f.state || ""} onChange={inp("state")} /></F>
          </div>
          <F label="Title"><Input data-testid="guide-title" value={f.title} onChange={inp("title")} /></F>
          <F label="Cover image"><ImageUploader testId="guide-image" value={f.image} onChange={set("image")} /></F>
          <F label="Summary"><Textarea rows={2} value={f.summary} onChange={inp("summary")} /></F>
          <F label="Best time to visit"><Input data-testid="guide-best-time" value={f.best_time} onChange={inp("best_time")} /></F>
          <F label="Route tips (rich text)"><HtmlEditor testId="guide-route" value={f.route_tips} onChange={set("route_tips")} /></F>
          <F label="Guide content (rich text)"><HtmlEditor testId="guide-content" value={f.content} onChange={set("content")} /></F>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.published !== false} onChange={(e) => set("published")(e.target.checked)} /> Published</label>
        </TabsContent>
        <TabsContent value="hi" className="space-y-3 mt-3">
          <F label="शीर्षक"><Input value={f.title_hi} onChange={inp("title_hi")} /></F>
          <F label="सारांश"><Textarea rows={2} value={f.summary_hi} onChange={inp("summary_hi")} /></F>
          <F label="सर्वोत्तम समय"><Input value={f.best_time_hi} onChange={inp("best_time_hi")} /></F>
          <F label="मार्ग सुझाव"><HtmlEditor value={f.route_tips_hi} onChange={set("route_tips_hi")} /></F>
          <F label="सामग्री"><HtmlEditor value={f.content_hi} onChange={set("content_hi")} /></F>
        </TabsContent>
        <TabsContent value="faq" className="space-y-3 mt-3">
          {f.faqs.map((q, i) => (
            <div key={i} className="bg-white rounded-lg p-3 border border-gold/15 space-y-2">
              <div className="flex justify-between items-center"><span className="text-xs font-semibold text-indigo-deep/60">FAQ {i + 1}</span><Button size="sm" variant="ghost" onClick={() => set("faqs")(f.faqs.filter((_, j) => j !== i))}><Trash2 className="w-3.5 h-3.5 text-red-600" /></Button></div>
              <Input data-testid={`faq-q-${i}`} placeholder="Question" value={q.q} onChange={(e) => setFaq(i, "q", e.target.value)} />
              <Textarea rows={2} placeholder="Answer" value={q.a} onChange={(e) => setFaq(i, "a", e.target.value)} />
              <Input placeholder="प्रश्न (हिंदी)" value={q.q_hi || ""} onChange={(e) => setFaq(i, "q_hi", e.target.value)} />
              <Textarea rows={2} placeholder="उत्तर (हिंदी)" value={q.a_hi || ""} onChange={(e) => setFaq(i, "a_hi", e.target.value)} />
            </div>
          ))}
          <Button variant="outline" data-testid="faq-add" onClick={() => set("faqs")([...f.faqs, { q: "", a: "", q_hi: "", a_hi: "" }])} className="border-gold/40"><Plus className="w-4 h-4 mr-1" />Add FAQ</Button>
        </TabsContent>
        <TabsContent value="seo" className="space-y-3 mt-3">
          <F label="SEO title"><Input value={f.seo_title || ""} onChange={inp("seo_title")} maxLength={70} /></F>
          <F label="Meta description"><Textarea rows={3} value={f.seo_desc || ""} onChange={inp("seo_desc")} maxLength={160} /></F>
        </TabsContent>
      </Tabs>
      <Button data-testid="guide-save" onClick={save} className="w-full bg-indigo-deep text-white mt-2">Save guide</Button>
    </DialogContent>
  );
}
